import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Phone } from "lucide-react";
import { toast } from "sonner";
import { PODA_TIPOS, MAP_CENTER } from "@/mock";
import { getFechasDisponibles, adminCrearSolicitudTelefono } from "@/api";
import { getErrorMessage } from "@/lib/errors";

const emptyForm = {
  tipo_servicio: "enseres",
  nombre_completo: "", dni_solicitante: "", email: "", telefono: "",
  enseres_texto: "", es_mueble_grande: false,
  tipo_vegetacion: "", cantidad_bultos: "",
  direccion_completa: "", isla_contenedores: "", fecha_preferida: "",
  pagador_nombre: "", pagador_apellidos: "", pagador_dni: "",
  pagador_domicilio: "", pagador_numero: "", pagador_piso: "", pagador_puerta: "",
  aceptacion_lopd: false, acepta_normas: false,
};

export default function SolicitudTelefonoDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(emptyForm);
  const [fechas, setFechas] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  useEffect(() => {
    if (!open) return;
    getFechasDisponibles(form.tipo_servicio).then(setFechas).catch(() => setFechas([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, form.tipo_servicio]);

  const handleClose = () => {
    setForm(emptyForm);
    onClose();
  };

  const canSubmit =
    form.nombre_completo && form.dni_solicitante && form.email && form.telefono &&
    form.direccion_completa && form.isla_contenedores && form.fecha_preferida &&
    form.pagador_nombre && form.pagador_apellidos && form.pagador_dni &&
    form.pagador_domicilio && form.pagador_numero &&
    form.aceptacion_lopd && form.acepta_normas &&
    (form.tipo_servicio === "enseres"
      ? (form.es_mueble_grande ? form.enseres_texto.trim().length > 0 : form.enseres_texto.trim().length > 0)
      : form.tipo_vegetacion && Number(form.cantidad_bultos) > 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const enseres_lista = form.es_mueble_grande
        ? [form.enseres_texto.trim()]
        : form.enseres_texto.split("\n").map((l) => l.trim()).filter(Boolean);

      const payload = {
        tipo_servicio: form.tipo_servicio,
        nombre_completo: form.nombre_completo,
        dni_solicitante: form.dni_solicitante,
        email: form.email,
        telefono: form.telefono,
        aceptacion_lopd: form.aceptacion_lopd,
        acepta_normas: form.acepta_normas,
        enseres_lista: form.tipo_servicio === "enseres" ? enseres_lista : null,
        es_mueble_grande: form.tipo_servicio === "enseres" ? form.es_mueble_grande : false,
        tipo_vegetacion: form.tipo_servicio === "poda" ? form.tipo_vegetacion : null,
        cantidad_bultos: form.tipo_servicio === "poda" ? Number(form.cantidad_bultos) : null,
        detalles: "Solicitud registrada por teléfono por el personal de Valoriza/Ayuntamiento.",
        direccion_completa: form.direccion_completa,
        isla_contenedores: form.isla_contenedores,
        latitude: MAP_CENTER[0],
        longitude: MAP_CENTER[1],
        fecha_preferida: form.fecha_preferida,
        pagador_nombre: form.pagador_nombre,
        pagador_apellidos: form.pagador_apellidos,
        pagador_dni: form.pagador_dni,
        pagador_domicilio: form.pagador_domicilio,
        pagador_numero: form.pagador_numero,
        pagador_piso: form.pagador_piso,
        pagador_puerta: form.pagador_puerta,
      };
      await adminCrearSolicitudTelefono(payload);
      toast.success("Solicitud telefónica registrada correctamente");
      onCreated?.();
      handleClose();
    } catch (err) {
      toast.error(getErrorMessage(err, "No se pudo registrar la solicitud"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="solicitud-telefono-dialog">
        <DialogHeader>
          <DialogTitle className="text-municipal font-heading flex items-center gap-2">
            <Phone className="w-5 h-5" /> Nueva solicitud telefónica
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <RadioGroup value={form.tipo_servicio} onValueChange={(v) => update({ tipo_servicio: v, fecha_preferida: "" })} className="grid grid-cols-2 gap-3">
            <div className="flex items-center space-x-2 border rounded-lg px-3 py-2">
              <RadioGroupItem value="enseres" id="tel-enseres" />
              <Label htmlFor="tel-enseres" className="font-normal cursor-pointer">Enseres</Label>
            </div>
            <div className="flex items-center space-x-2 border rounded-lg px-3 py-2">
              <RadioGroupItem value="poda" id="tel-poda" />
              <Label htmlFor="tel-poda" className="font-normal cursor-pointer">Poda</Label>
            </div>
          </RadioGroup>

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Nombre y apellidos</Label><Input value={form.nombre_completo} onChange={(e) => update({ nombre_completo: e.target.value })} data-testid="tel-nombre-input" /></div>
            <div><Label className="text-xs mb-1 block">DNI/NIE solicitante</Label><Input value={form.dni_solicitante} onChange={(e) => update({ dni_solicitante: e.target.value.toUpperCase() })} data-testid="tel-dni-input" /></div>
            <div><Label className="text-xs mb-1 block">Email</Label><Input type="email" value={form.email} onChange={(e) => update({ email: e.target.value })} data-testid="tel-email-input" /></div>
            <div><Label className="text-xs mb-1 block">Teléfono</Label><Input value={form.telefono} onChange={(e) => update({ telefono: e.target.value })} data-testid="tel-telefono-input" /></div>
          </div>

          {form.tipo_servicio === "enseres" ? (
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Checkbox id="tel-grande" checked={form.es_mueble_grande} onCheckedChange={(v) => update({ es_mueble_grande: v })} />
                <Label htmlFor="tel-grande" className="font-normal text-sm cursor-pointer">Es 1 mueble de grandes dimensiones (en lugar de hasta 3 enseres)</Label>
              </div>
              <Label className="text-xs block">{form.es_mueble_grande ? "Descripción del mueble" : "Enseres (uno por línea, máx. 3)"}</Label>
              <Textarea value={form.enseres_texto} onChange={(e) => update({ enseres_texto: e.target.value })} data-testid="tel-enseres-textarea" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Tipo de resto vegetal</Label>
                <Select value={form.tipo_vegetacion} onValueChange={(v) => update({ tipo_vegetacion: v })}>
                  <SelectTrigger data-testid="tel-tipo-vegetacion-select"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                  <SelectContent>{PODA_TIPOS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs mb-1 block">Nº de bolsas/atillos</Label><Input type="number" value={form.cantidad_bultos} onChange={(e) => update({ cantidad_bultos: e.target.value })} data-testid="tel-cantidad-input" /></div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-3">
            <div><Label className="text-xs mb-1 block">Dirección del solicitante</Label><Input value={form.direccion_completa} onChange={(e) => update({ direccion_completa: e.target.value })} data-testid="tel-direccion-input" /></div>
            <div><Label className="text-xs mb-1 block">Isla de contenedores</Label><Input value={form.isla_contenedores} onChange={(e) => update({ isla_contenedores: e.target.value })} data-testid="tel-isla-input" /></div>
          </div>

          <div>
            <Label className="text-xs mb-1 block">Fecha de recogida</Label>
            <Select value={form.fecha_preferida} onValueChange={(v) => update({ fecha_preferida: v })}>
              <SelectTrigger data-testid="tel-fecha-select"><SelectValue placeholder="Selecciona una fecha disponible" /></SelectTrigger>
              <SelectContent>{fechas.map((f) => <SelectItem key={f.fecha} value={f.fecha}>{f.dia_semana} {f.fecha} ({f.plazas_libres} plazas)</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div className="pt-2 border-t border-gray-100">
            <p className="text-sm font-semibold text-municipal mb-2">Datos del pagador de la tasa de basuras</p>
            <div className="grid sm:grid-cols-2 gap-3 mb-3">
              <div><Label className="text-xs mb-1 block">Nombre</Label><Input value={form.pagador_nombre} onChange={(e) => update({ pagador_nombre: e.target.value })} data-testid="tel-pagador-nombre-input" /></div>
              <div><Label className="text-xs mb-1 block">Apellidos</Label><Input value={form.pagador_apellidos} onChange={(e) => update({ pagador_apellidos: e.target.value })} data-testid="tel-pagador-apellidos-input" /></div>
            </div>
            <div className="mb-3"><Label className="text-xs mb-1 block">DNI/NIE pagador</Label><Input value={form.pagador_dni} onChange={(e) => update({ pagador_dni: e.target.value.toUpperCase() })} data-testid="tel-pagador-dni-input" /></div>
            <div className="mb-3"><Label className="text-xs mb-1 block">Calle</Label><Input value={form.pagador_domicilio} onChange={(e) => update({ pagador_domicilio: e.target.value })} data-testid="tel-pagador-domicilio-input" /></div>
            <div className="grid sm:grid-cols-3 gap-3">
              <div><Label className="text-xs mb-1 block">Número</Label><Input value={form.pagador_numero} onChange={(e) => update({ pagador_numero: e.target.value })} data-testid="tel-pagador-numero-input" /></div>
              <div><Label className="text-xs mb-1 block">Piso</Label><Input value={form.pagador_piso} onChange={(e) => update({ pagador_piso: e.target.value })} data-testid="tel-pagador-piso-input" /></div>
              <div><Label className="text-xs mb-1 block">Puerta</Label><Input value={form.pagador_puerta} onChange={(e) => update({ pagador_puerta: e.target.value })} data-testid="tel-pagador-puerta-input" /></div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox id="tel-lopd" checked={form.aceptacion_lopd} onCheckedChange={(v) => update({ aceptacion_lopd: v })} data-testid="tel-lopd-checkbox" />
            <Label htmlFor="tel-lopd" className="font-normal text-sm cursor-pointer">El ciudadano acepta la política de privacidad</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox id="tel-normas" checked={form.acepta_normas} onCheckedChange={(v) => update({ acepta_normas: v })} data-testid="tel-normas-checkbox" />
            <Label htmlFor="tel-normas" className="font-normal text-sm cursor-pointer">Se han leído las normas del servicio al ciudadano y las acepta</Label>
          </div>

          <Button type="submit" disabled={!canSubmit || submitting} className="w-full bg-municipal hover:bg-municipal-dark text-white" data-testid="tel-submit-btn">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Registrar solicitud telefónica
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
